import React, { useState, useEffect } from 'react';
import { motion, useScroll, useTransform, useInView } from 'motion/react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  ArrowRight,
  Building2,
  Users,
  Package,
  FolderOpen,
  CheckCircle,
  Star,
  TrendingUp,
  CreditCard,
  Zap,
  Shield,
  Settings,
  Check,
  Store,
  ShoppingCart,
  Boxes,
  FileText,
  DollarSign,
  MessageSquare,
  Camera,
  Bell,
  Truck,
  Repeat,
  LineChart,
  Workflow,
  Search,
  ClipboardList,
  MapPin,
  RefreshCw,
  Sparkles,
  Eye,
  Network,
  Upload,
  BarChart2,
  BookOpen
} from 'lucide-react';
import { AppIcon } from '../components/AppIcon';
import type { IconName } from '../assets/icons';

const CounterAnimation = ({ value, duration = 2000 }: { value: number; duration?: number }) => {
  const [count, setCount] = useState(0);
  const ref = React.useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true });

  useEffect(() => {
    if (isInView) {
      let startTime: number;
      const animate = (timestamp: number) => {
        if (!startTime) startTime = timestamp;
        const progress = Math.min((timestamp - startTime) / duration, 1);
        setCount(Math.floor(progress * value));
        if (progress < 1) {
          requestAnimationFrame(animate);
        }
      };
      requestAnimationFrame(animate);
    }
  }, [isInView, value, duration]);

  return <span ref={ref}>{count}</span>;
};

const FeatureSection = ({ children, className = '' }: { children: React.ReactNode; className?: string }) => {
  return (
    <motion.section
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.8 }}
      className={`py-16 md:py-24 ${className}`}
    >
      {children}
    </motion.section>
  );
};

const VendorDashboardMockup = () => {
  return (
    <div className="relative">
      {/* Floating Background Icons */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[
          { Icon: Store, delay: 0, x: '10%', y: '15%' },
          { Icon: ShoppingCart, delay: 0.5, x: '85%', y: '20%' },
          { Icon: Package, delay: 1, x: '15%', y: '70%' },
          { Icon: TrendingUp, delay: 1.5, x: '80%', y: '75%' },
          { Icon: DollarSign, delay: 2, x: '50%', y: '10%' },
          { Icon: Boxes, delay: 2.5, x: '90%', y: '50%' }
        ].map((item, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, scale: 0 }}
            animate={{
              opacity: [0, 0.15, 0],
              scale: [0, 1, 0],
              y: [0, -30, 0]
            }}
            transition={{
              duration: 4,
              repeat: Infinity,
              delay: item.delay,
              ease: "easeInOut"
            }}
            className="absolute"
            style={{ left: item.x, top: item.y }}
          >
            <item.Icon className="w-16 h-16 text-blue-600" />
          </motion.div>
        ))}
      </div>

      {/* Main Dashboard Mockup */}
      <motion.div
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden relative z-10"
      >
        <div className="bg-[#f5f5f7] px-5 py-3.5 border-b border-gray-200/80">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3.5 min-w-0">
              <div className="flex items-center gap-2 flex-shrink-0">
                <div className="w-3 h-3 rounded-full bg-[#ff5f57]"></div>
                <div className="w-3 h-3 rounded-full bg-[#febc2e]"></div>
                <div className="w-3 h-3 rounded-full bg-[#28c840]"></div>
              </div>
              <h3 className="font-medium text-gray-700 text-sm md:text-base truncate">
                Vendor Portal Dashboard
              </h3>
            </div>
            <div className="flex items-center gap-4 flex-shrink-0 text-gray-500">
              <Search className="w-4 h-4" />
              <Bell className="w-4 h-4" />
              <div className="w-7 h-7 rounded-full bg-gray-300"></div>
            </div>
          </div>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <Card className="p-4">
              <div className="text-2xl font-bold text-blue-600">
                <CounterAnimation value={47} />
              </div>
              <p className="text-sm text-gray-600">Active Orders</p>
            </Card>
            <Card className="p-4">
              <div className="text-2xl font-bold text-green-600">
                $<CounterAnimation value={12450} />
              </div>
              <p className="text-sm text-gray-600">Monthly Revenue</p>
            </Card>
            <Card className="p-4">
              <div className="text-2xl font-bold" style={{ color: '#f58b01' }}>
                <CounterAnimation value={156} />
              </div>
              <p className="text-sm text-gray-600">Products Listed</p>
            </Card>
            <Card className="p-4">
              <div className="text-2xl font-bold text-purple-600">
                <CounterAnimation value={23} />
              </div>
              <p className="text-sm text-gray-600">Connected Brokerages</p>
            </Card>
          </div>

          <div className="space-y-3">
            {[
              { order: 'Premium Yard Sign - Set of 10', client: 'Apex Realty', status: 'in-production', action: 'Due Tomorrow' },
              { order: 'Rider Signs - Custom Print', client: 'Century Homes', status: 'ready', action: 'Ready to Ship' },
              { order: 'Installation Service - 5 Locations', client: 'Blue Sky Brokers', status: 'scheduled', action: 'Install on 10/12' },
              { order: 'Open House Signs - Rush Order', client: 'Premier Properties', status: 'pending', action: 'Awaiting Payment' }
            ].map((order, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5 + index * 0.1 }}
                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <Package className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="font-medium text-gray-900">{order.order}</p>
                    <p className="text-sm text-gray-600">{order.client} • {order.action}</p>
                  </div>
                </div>
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.7 + index * 0.1 }}
                  className={`px-3 py-1 rounded-full text-sm font-medium ${order.status === 'in-production' ? 'bg-blue-100 text-blue-800' :
                    order.status === 'ready' ? 'bg-green-100 text-green-800' :
                      order.status === 'scheduled' ? 'bg-purple-100 text-purple-800' :
                        'bg-yellow-100 text-yellow-800'
                    }`}
                >
                  {order.status === 'in-production' ? 'Production' :
                    order.status === 'ready' ? 'Ready' :
                      order.status === 'scheduled' ? 'Scheduled' :
                        'Pending'}
                </motion.div>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default function VendorSolutionsPage() {
  return (
    <div className="min-h-screen bg-gray-50">
      <VendorContent />
    </div>
  );
}

function VendorContent() {
  const { scrollYProgress } = useScroll();
  const backgroundY = useTransform(scrollYProgress, [0, 1], ['0%', '50%']);

  const whyVendorsChoose = [
    {
      icon: Network,
      number: '1',
      title: 'Built-In Customer Base',
      description: 'Tap directly into a network of brokerages and agents who use RealEsy daily for signage, photography, staging, print, and installation services.'
    },
    {
      icon: Eye,
      number: '2',
      title: 'Real Visibility, Real Growth',
      description: 'Get your brand in front of active buyers — not cold leads. Every order, message, and job request happens inside RealEsy\'s marketplace.'
    },
    {
      icon: Zap,
      number: '3',
      title: 'Automation That Saves You Time',
      description: 'Forget email chains and spreadsheets. Automate orders, invoices, and updates with built-in tools that sync everything in real time.'
    },
    {
      icon: Shield,
      number: '4',
      title: 'Secure Payments. Faster Cash Flow.',
      description: 'Get paid through Stripe with full accounting integration. No manual reconciliation. No chasing payments.'
    },
    {
      icon: BarChart2,
      number: '5',
      title: 'Operational Clarity',
      description: 'See your entire pipeline at a glance — from sales orders to active service orders like photography or installations. Track what\'s pending, fulfilled, or awaiting proof — including uploaded photos, notes, and completion details.'
    }
  ];

  const coreFeatures: { icon: IconName; title: string; features: string[] }[] = [
    {
      icon: 'commerceConnectedVendorPortal',
      title: 'Commerce-Connected Vendor Portal',
      features: [
        'Create your own branded vendor store inside RealEsy',
        'Add SKUs, pricing, volume discounts, and brokerage-specific fees',
        'Orders appear instantly in your dashboard for fulfillment'
      ]
    },
    {
      icon: 'cmsTools',
      title: 'E-Commerce CMS Tools',
      features: [
        'Upload listings with photos and stock details',
        'Automate tax, shipping, and invoicing',
        'Generate branded invoices with one click'
      ]
    },
    {
      icon: 'serviceOrderIntegration',
      title: 'Service Order Integration',
      features: [
        'Sell both products and services in one platform — signage, installations, photography, and more.',
        'Link purchased items to future service jobs for seamless fulfillment.',
        'Upload proof, assign teams, and track progress with full visibility.',
        'Keep sales and service data connected for full visibility — without re-entering details across multiple systems.'
      ]
    },
    {
      icon: 'assetsInventoryManagement',
      title: 'Asset & Inventory Management',
      features: [
        'Track what\'s in stock, what\'s deployed, and what\'s returned',
        'Assign assets to jobs and restock automatically',
        'Get full lifecycle visibility from warehouse to install'
      ]
    },
    {
      icon: 'accountingPayment',
      title: 'Accounting & Payments',
      features: [
        'Sync with QuickBooks, Xero, and other accounting tools',
        'Auto-generate invoices with taxes and rush fees',
        'Secure, instant Stripe payments with full reconciliation'
      ]
    },
    {
      icon: 'mobileAppForVendors',
      title: 'Mobile App for Vendors',
      features: [
        'Manage everything from your phone',
        'Receive job alerts, update progress, upload photos',
        'Chat directly with agents and brokerages'
      ]
    },
    {
      icon: 'vendorReportingInsights',
      title: 'Reporting & Insights',
      features: [
        'View real-time dashboards of sales, services, and stock',
        'Identify your top clients, most profitable products, and seasonal trends',
        'Export data anytime for accounting or team reviews'
      ]
    }
  ];

  const outcomes: { icon: IconName; title: string; description: string }[] = [
    { icon: 'gainVisibilityInstantly', title: 'Gain Visibility — Instantly', description: 'Your storefront lives where your customers already work — inside RealEsy.' },
    { icon: 'reachNewBrokerages', title: 'Reach New Brokerages Without Marketing Spend', description: 'Appear directly in search results when agents or offices order signage, print, or staging services.' },
    { icon: 'automatedOperations', title: 'Automate Operations', description: 'Save hours each week — RealEsy syncs orders, inventory, and service jobs automatically.' },
    { icon: 'increaseRevenue', title: 'Increase Revenue', description: 'Drive repeat business with volume discounts, brokerage-specific pricing, and subscription packages.' },
    { icon: 'getPaidFaster', title: 'Get Paid Faster', description: 'Secure payments and automatic reconciliation remove delays and disputes.' },
    { icon: 'scaleSmarter', title: 'Scale Smarter', description: 'Add products, manage services, and grow — without hiring more staff or adding new software.' }
  ];

  const faqs = [
    {
      question: 'Who can join as a vendor on RealEsy?',
      answer: 'Any company offering products or services to brokerages or agents — signage, staging, print, photography, marketing, logistics, and more.'
    },
    {
      question: 'How does RealEsy help vendors grow?',
      answer: 'We connect you directly with brokerages and agents already using the platform — automating sales, orders, and billing so you spend less time managing and more time fulfilling.'
    },
    {
      question: 'How do payments work?',
      answer: 'All payments flow securely through Stripe, with optional QuickBooks or Xero sync for reconciliation.'
    },
    {
      question: 'Can I offer custom pricing or volume discounts?',
      answer: 'Yes — set up special pricing for specific brokerages, offices, or groups to drive recurring business.'
    },
    {
      question: 'Is there a mobile app?',
      answer: 'Absolutely. Manage your operations, chat with clients, and track jobs anywhere.'
    }
  ];

  return (
    <>
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-50 via-white to-blue-50 py-20 md:py-32 overflow-hidden">
        <motion.div
          style={{ y: backgroundY }}
          className="absolute inset-0 opacity-30"
        >
          <div className="absolute top-20 left-10 w-64 h-64 bg-blue-200 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-200 rounded-full blur-3xl"></div>
        </motion.div>

        {/* Floating Background Icons */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[
            { Icon: Store, delay: 0, x: '10%', y: '15%' },
            { Icon: ShoppingCart, delay: 0.5, x: '85%', y: '20%' },
            { Icon: Package, delay: 1, x: '15%', y: '70%' },
            { Icon: TrendingUp, delay: 1.5, x: '80%', y: '75%' },
            { Icon: DollarSign, delay: 2, x: '50%', y: '10%' },
            { Icon: Boxes, delay: 2.5, x: '90%', y: '50%' }
          ].map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0 }}
              animate={{
                opacity: [0, 0.15, 0],
                scale: [0, 1, 0],
                y: [0, -30, 0]
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                delay: item.delay,
                ease: "easeInOut"
              }}
              className="absolute"
              style={{ left: item.x, top: item.y }}
            >
              <item.Icon className="w-16 h-16 text-blue-600" />
            </motion.div>
          ))}
        </div>

        <div className="container mx-auto px-6 relative">
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <div className="max-w-6xl mx-auto space-y-8">
                <div className="space-y-4">
                  <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-blue-800 leading-tight">
                    Your Business. Connected to Every Brokerage.
                  </h1>
                </div>

                <div className="max-w-4xl mx-auto space-y-6">
                  <p className="text-lg md:text-xl lg:text-2xl text-gray-700 leading-relaxed font-medium">
                    RealEsy isn't just software — it's your growth engine.
                  </p>
                  <p className="text-base md:text-lg text-gray-700 leading-relaxed">
                    Join a platform where brokerages and agents already manage every part of their business — and where vendors like you get instant visibility, consistent orders, and real revenue growth.
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center items-center pt-8">
                  <Button
                    size="lg"
                    className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-8 sm:px-12 py-5 sm:py-6 text-base sm:text-lg font-bold rounded-xl shadow-2xl hover:shadow-3xl transform hover:scale-[1.02] transition-all duration-300 w-full sm:w-auto border-0"
                  >
                    Become a RealEsy Vendor
                    <ArrowRight className="ml-3 w-5 h-5" />
                  </Button>
                </div>
              </div>
            </motion.div>

            <div className="max-w-5xl mx-auto">
              <VendorDashboardMockup />
            </div>
          </div>
        </div>
      </section>

      {/* Value Proposition - Highlighted Section */}
      <FeatureSection className="bg-gradient-to-br from-orange-50 via-amber-50 to-orange-50 border-y-4 border-orange-200">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="max-w-5xl mx-auto"
          >
            <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12 border-2 border-orange-200">
              <div className="text-center space-y-6">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-100 rounded-full">
                  <Sparkles className="w-5 h-5" style={{ color: '#f58b01' }} />
                  <span className="font-medium" style={{ color: '#f58b01' }}>The RealEsy Difference</span>
                </div>
                <h2 className="text-2xl md:text-4xl font-bold text-gray-900">
                  Unlike schedulers that stop at the transaction,<br />RealEsy puts your business in front of the people who actually make decisions.
                </h2>
                <p className="text-lg md:text-xl text-gray-700 leading-relaxed">
                  We bring the clients — you focus on delivering your best work.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </FeatureSection>

      {/* Why Vendors Choose RealEsy - Numbered List */}
      <FeatureSection className="bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-6">
                Why Vendors Choose RealEsy
              </h2>
              <p className="text-lg md:text-xl text-gray-700 max-w-3xl mx-auto">
                Grow faster with visibility, automation, and real estate–ready tools.
              </p>
            </div>

            <div className="space-y-6">
              {whyVendorsChoose.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ x: 8, boxShadow: "0 20px 40px rgba(0,0,0,0.1)" }}
                  className="bg-gradient-to-r from-blue-50 to-white p-6 md:p-8 rounded-2xl border-2 border-blue-200 group cursor-pointer"
                >
                  <div className="flex items-start gap-6">
                    <div className="flex-shrink-0">
                      <div
                        className="w-16 h-16 rounded-2xl flex items-center justify-center text-white text-2xl font-bold shadow-lg"
                        style={{ backgroundColor: '#f58b01' }}
                      >
                        {item.number}
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <item.icon className="w-6 h-6 text-blue-600" />
                        <h3 className="text-xl md:text-2xl font-bold text-gray-900">{item.title}</h3>
                      </div>
                      <p className="text-gray-600 leading-relaxed text-base md:text-lg">{item.description}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </FeatureSection>

      {/* Core Features Section */}
      <FeatureSection className="bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-6">
                Core Features — Everything You Need to Run & Grow Your Business
              </h2>
              <p className="text-lg md:text-xl text-gray-700 max-w-3xl mx-auto">
                A complete vendor management platform built for real estate.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {coreFeatures.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ y: -8, boxShadow: "0 20px 40px rgba(0,0,0,0.15)" }}
                  className="bg-white p-8 rounded-2xl border-2 border-gray-200 group cursor-pointer hover:border-blue-300 transition-colors"
                >
                  <div className="w-16 h-16 bg-white rounded-2xl shadow-md border border-gray-200 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                    <AppIcon name={feature.icon} size={36} alt={feature.title} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">{feature.title}</h3>
                  <ul className="space-y-3">
                    {feature.features.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <Check className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-600 leading-relaxed">{item}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </FeatureSection>

      {/* Outcomes & Benefits Section */}
      <FeatureSection className="bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-6">
                Outcomes & Benefits
              </h2>
              <p className="text-lg md:text-xl text-gray-700 max-w-3xl mx-auto">
                Real results for vendors who choose RealEsy.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {outcomes.map((outcome, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  whileHover={{ y: -8, boxShadow: "0 20px 40px rgba(0,0,0,0.1)" }}
                  className="bg-gradient-to-br from-blue-50 to-purple-50 p-8 rounded-2xl border border-gray-200 group cursor-pointer"
                >
                  <div className="w-14 h-14 bg-white rounded-2xl shadow-md border border-gray-200 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                    <AppIcon name={outcome.icon} size={30} alt={outcome.title} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{outcome.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{outcome.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </FeatureSection>

      {/* Smarter Marketplace Section - Highlighted */}
      <FeatureSection className="text-white bg-gradient-to-r from-[#236DD9] via-[#1B28BC] to-[#073399]">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="max-w-5xl mx-auto text-center"
          >
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 rounded-full backdrop-blur-sm">
                <BookOpen className="w-5 h-5 text-blue-300" />
                <span className="font-medium text-blue-200">A Smarter Way to Grow</span>
              </div>
              <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold leading-tight">
                A Smarter Marketplace for Vendors Who Want to Grow
              </h2>
              <div className="space-y-4 text-lg md:text-xl text-blue-100">
                <p>Most schedulers stop at booking. RealEsy starts where they stop.</p>
                <p>We connect you with real, active brokerages — not forms or leads.</p>
                <p>Your products get discovered, your services get booked, and your business grows automatically.</p>
              </div>
              <div className="pt-4">
                <p className="text-xl md:text-2xl font-bold text-white">
                  Less chasing. More selling. More visibility.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </FeatureSection>

      {/* FAQs Section */}
      <FeatureSection className="bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-4">
                Frequently Asked Questions
              </h2>
              <p className="text-base md:text-lg text-gray-600">
                Get answers to common questions about becoming a RealEsy vendor.
              </p>
            </div>

            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-shadow"
                >
                  <div className="flex items-center gap-4">
                    <div className="flex-shrink-0">
                      <div className="flex items-center justify-center">
                        <AppIcon name="faqQuestion" size={28} alt="FAQ" />
                      </div>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-base md:text-lg font-medium text-gray-900 mb-2">
                        {faq.question}
                      </h3>
                      <p className="text-sm md:text-base text-gray-600 leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </FeatureSection>

      {/* Final CTA */}
      <section className="relative bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 py-24 overflow-hidden">
        <motion.div
          style={{ y: backgroundY }}
          className="absolute inset-0 opacity-20"
        >
          {[...Array(6)].map((_, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, scale: 0 }}
              animate={{
                opacity: [0, 1, 0],
                scale: [0, 1, 0],
                x: Math.random() * 800,
                y: Math.random() * 600
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                delay: i * 0.8,
                ease: "easeInOut"
              }}
              className="absolute w-8 h-8"
            >
              {i % 3 === 0 ?
                <Store className="w-full h-full text-white" /> :
                i % 3 === 1 ?
                  <ShoppingCart className="w-full h-full text-white" /> :
                  <div className="w-full h-full bg-white rounded-full opacity-60"></div>
              }
            </motion.div>
          ))}
        </motion.div>

        <div className="container mx-auto px-6 relative">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6 leading-tight">
              Grow Your Real Estate Vendor Business with RealEsy
            </h2>
            <p className="text-lg md:text-xl text-blue-100 mb-8 leading-relaxed">
              Sell smarter. Fulfill faster. Get seen by every brokerage you want to work with.
            </p>
            <p className="text-base md:text-lg text-blue-200 mb-12 leading-relaxed">
              RealEsy — where real estate vendors grow their business, not just manage it.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center items-center">
              <Button
                size="lg"
                className="bg-white text-blue-700 hover:bg-blue-50 hover:text-blue-800 px-8 sm:px-12 py-5 sm:py-6 text-base sm:text-lg font-bold rounded-xl shadow-2xl hover:shadow-3xl transform hover:scale-[1.02] transition-all duration-300 border-2 border-white w-full sm:w-auto"
              >
                Become a RealEsy Vendor
                <ArrowRight className="ml-3 w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
